import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import {
  useMeta,
  useSubmissionCounts,
  useSubmissions,
  useWorkCenters,
} from '@/lib/queries'
import { useAuth } from '@/context/AuthContext'
import { Button, Card, EmptyState, ErrorState, Input, Spinner } from '@/components/ui'
import { ExportButton } from '@/components/ExportButton'
import { DataPager } from '@/components/DataPager'
import { Hint } from '@/components/Hint'
import { FilterBar } from '@/components/submissions/FilterBar'
import { QueueTabs } from '@/components/submissions/QueueTabs'
import { SubmissionCards, SubmissionTable } from '@/components/submissions/SubmissionRows'
import { useDebounced } from '@/lib/useDebounced'
import { cn } from '@/lib/utils'
import {
  activeFilterCount,
  clearFilters,
  countFilters,
  listFilters,
  parseQueue,
  serializeQueue,
  toggleSort,
  updateQueue,
  type QueueState,
} from '@/lib/submissionQueue'
import { useT } from '@/i18n/I18nProvider'
import type { EnumOption } from '@/types/api'

/**
 * The four status names the chain hint reads out, looked up in `/api/meta`.
 *
 * The sentence lives in the dictionary; the words "Nova" and "Zatvorena" do
 * not, and must not — statuses are the server's in four languages, and a copy
 * of one in a translation file is the drift this whole layer exists to prevent.
 * So the hint holds placeholders, they are filled here, and if `meta` has not
 * arrived the hint is not shown at all rather than shown with holes in it.
 *
 * SubmissionDetail.tsx carries the same lookup, which is deliberate and
 * not laziness: exporting it from here would make this module export something
 * other than a component, which costs fast refresh on the whole page for the
 * sake of one `find`. lib/utils.ts is not an option either — `shadcn init`
 * rewrites it, and it has no business knowing about `/api/meta`.
 */
function statusChain(options: EnumOption[] | undefined) {
  const label = (value: string) => options?.find((option) => option.value === value)?.label

  const chain = {
    new: label('NEW'),
    inProgress: label('IN_PROGRESS'),
    resolved: label('RESOLVED'),
    closed: label('CLOSED'),
  }

  return Object.values(chain).every(Boolean) ? (chain as Record<string, string>) : null
}

/**
 * The reports, as queues.
 *
 * "What needs me now" is the tab row: each queue with its count, the default
 * one the open reports. Everything on the screen — tab, filters, order, page —
 * lives in the address bar (see lib/submissionQueue.ts), so a link from the
 * bell or from Početna opens on exactly the rows it described, and the back
 * button undoes a filter.
 */
export function SubmissionsPage() {
  const { user } = useAuth()
  const t = useT()
  const meta = useMeta()
  const workCenters = useWorkCenters()
  const isManager = user?.isManager === true

  const [params, setParams] = useSearchParams()
  const state = useMemo(() => parseQueue(params, isManager), [params, isManager])

  /*
   * Old links are rewritten in place, once, so the address bar always shows
   * the vocabulary the tabs use — and so "back" from a reminder link does not
   * land on a URL the screen then silently reinterprets.
   */
  useEffect(() => {
    const canonical = serializeQueue(state).toString()

    if (canonical !== params.toString()) setParams(canonical, { replace: true })
  }, [state, params, setParams])

  const change = (patch: Partial<QueueState>, replace = false) =>
    setParams(serializeQueue(updateQueue(state, patch)), { replace })

  /*
   * The search box types into local state and reaches the address bar after a
   * pause, so typing does not push a history entry per keystroke. Read back
   * from the URL when it changes underneath — the search palette's "every
   * report matching this" link lands here while the page is open.
   */
  const [search, setSearch] = useState(state.search)
  const debounced = useDebounced(search, 350)

  // Only a change that did not come from this box: echoing our own write
  // back could drop a letter typed while it was on its way.
  useEffect(() => {
    if (state.search !== debounced) setSearch(state.search)
  }, [state.search]) // eslint-disable-line react-hooks/exhaustive-deps

  // `change` is rebuilt every render; the debounced value is the trigger.
  useEffect(() => {
    if (debounced !== state.search) change({ search: debounced }, true)
  }, [debounced]) // eslint-disable-line react-hooks/exhaustive-deps

  const [perPage, setPerPage] = useState(25)

  const filters = listFilters(state)
  const query = useSubmissions({ ...filters, page: state.page, perPage })
  const counts = useSubmissionCounts(countFilters(state))

  const centres = workCenters.data ?? []
  const showPlant = new Set(centres.map((centre) => centre.plantId)).size > 1
  const chain = statusChain(meta.data?.submissionStatuses)
  const filtered = activeFilterCount(state) > 0 || Boolean(state.search)

  return (
    <div className="space-y-3">
      {/* One row on every size: the exports are small enough to sit beside
          the title, and on a phone a row of their own pushed the tabs — the
          point of the screen — further down. */}
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Every row carries a status badge and nothing on it says what the
              four words commit anybody to, so the title carries the hint. The
              subtitle is desk-only: on a phone it wrapped to three lines
              beside the exports and said nothing the tabs below do not. */}
          <h1 className="flex items-center gap-2 text-xl font-semibold sm:text-2xl">
            {t('submissions.list.title')}
            {chain && <Hint side="bottom">{t('submissions.status.chain.hint', chain)}</Hint>}
          </h1>
          <p className="muted hidden text-sm sm:block">
            {t(isManager ? 'submissions.list.subtitle.all' : 'submissions.list.subtitle.mine')}
          </p>
        </div>

        {/* The exports carry the CURRENT tab and filters, so what downloads
            matches what is on screen. The server applies them through the
            same code path as the list, so the two cannot disagree. */}
        <div className="flex shrink-0 gap-2">
          <ExportButton path="/exports/submissions.xlsx" format="xlsx" params={{ ...filters }} />
          <ExportButton path="/exports/submissions.pdf" format="pdf" params={{ ...filters }} />
        </div>
      </header>

      <QueueTabs state={state} counts={counts.data} isManager={isManager} />

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative lg:w-80 lg:shrink-0">
          <Search
            className="muted pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('submissions.list.search.placeholder')}
            className="pl-9"
            type="search"
            aria-label={t('submissions.list.search.label')}
          />
        </div>

        <FilterBar
          state={state}
          onChange={(patch) => change(patch)}
          types={meta.data?.submissionTypes ?? []}
          priorities={meta.data?.priorities ?? []}
          statuses={meta.data?.submissionStatuses ?? []}
          centres={centres}
          isManager={isManager}
        />
      </div>

      {query.isLoading && <Spinner />}

      {query.isError && (
        <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />
      )}

      {query.data && query.data.data.length === 0 && (
        <Card>
          {/* Two different empties: a queue with nothing in it says what
              would be here and how it gets here; a filtered list says the
              filters are why, and offers to drop them. */}
          {filtered ? (
            <EmptyState
              title={t('submissions.empty.filtered.title')}
              description={t('submissions.empty.filtered')}
              action={
                <Button variant="secondary" onClick={() => change(clearFilters(state))}>
                  {t('submissions.filter.clear')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              title={t('submissions.empty.title')}
              description={t(`submissions.empty.${state.tab}`)}
            />
          )}
        </Card>
      )}

      {query.data && query.data.data.length > 0 && (
        // Dimmed while the next page or filter loads, so the old rows on
        // screen do not read as the answer to the new question.
        <div
          aria-busy={query.isPlaceholderData}
          className={cn('space-y-3 transition-opacity', query.isPlaceholderData && 'opacity-60')}
        >
          {/*
            A table on a desk, cards in a hand. "Desk" is a fine pointer on a
            wide screen, not a width alone: a tablet on a stand is wide and is
            still driven by a thumb.
          */}
          <Card className="hidden p-0 lg:pointer-fine:block">
            <SubmissionTable
              rows={query.data.data}
              state={state}
              onSort={(column) => change(toggleSort(state, column))}
              showPlant={showPlant}
            />
          </Card>

          <div className="lg:pointer-fine:hidden">
            <SubmissionCards rows={query.data.data} showPlant={showPlant} />
          </div>

          <DataPager
            meta={query.data.meta}
            perPage={perPage}
            onPerPage={(value) => {
              setPerPage(value)
              change({ page: 1 })
            }}
            onPage={(page) => change({ page })}
          />
        </div>
      )}
    </div>
  )
}
